"use client";

import type { Voice } from "@/app/_data/voices";
import type { IUseCasePromptWithId } from "@components/languages/types";

import { getPromptsPerUseCase } from "@components/languages/textPrompts";
import { getVoiceToUseCasePromptAssignment } from "@components/languages/types";
import { PromptFeature } from "@components/languages/types";
import { SliderGeneric } from "@ui/components/composers/SliderGeneric";
import { Avatar } from "@ui/components/core/Avatar";
import { Button } from "@ui/components/core/Button";
import { getVoiceImage } from "@ui/components/helpers/voiceUtils";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { cn } from "@ui/lib/utils";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SampleOption } from "../../SamplePicker";
import type { DemoPrompts, SamplePrompt } from "../config";
import type { PlayingState } from "../constants";

import { SamplePicker } from "../../SamplePicker";

const EMPTY_SAMPLE_PROMPTS: SamplePrompt[] = [];

const SamplePlayButton = ({
  activeSampleId,
  playingState,
  prompt,
}: {
  activeSampleId?: string;
  playingState: "idle" | "loading" | "paused" | "playing";
  prompt: IUseCasePromptWithId;
}) => {
  const t = useTranslations("ClientKeys.TTS");

  return (
    <Button asChild size='md'>
      <div className='flex items-center gap-8 text-inverse'>
        {activeSampleId === prompt.id ? (
          playingState === "loading" ? (
            <>
              <Spinner20 className='animate-spin' />
            </>
          ) : playingState === "playing" ? (
            <>
              <Pause20 />
              <span className='f-ui-04'>{t("widget.pause")}</span>
            </>
          ) : (
            <>
              <Play20 />
              <span className='f-ui-04'>{t("widget.try_this_voice")}</span>
            </>
          )
        ) : (
          <>
            <Play20 />
            <span className='f-ui-04'>{t("widget.try_this_voice")}</span>
          </>
        )}
      </div>
    </Button>
  );
};

const SampleSliderCard = ({
  assignedVoices,
  description,
  prompt,
  activeSampleId,
  handlePlayPause,
  playingState,
  onSelected,
}: {
  activeSampleId?: string;
  assignedVoices: Voice[];
  description: string;
  handlePlayPause: () => void;
  onSelected: (prompt: IUseCasePromptWithId, voices: Voice[]) => void;
  playingState: "idle" | "loading" | "paused" | "playing";
  prompt: IUseCasePromptWithId;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <m.button
      className='group flex h-full w-full flex-shrink-0 flex-col items-center justify-center gap-8'
      onClick={() => (prompt.id === activeSampleId ? handlePlayPause() : onSelected(prompt, assignedVoices))}
    >
      <div className='mb-8 flex w-full flex-col'>
        <div className='mb-8 flex items-center justify-center'>
          {assignedVoices.map((voice, voiceIndex) => (
            <Avatar
              className={cn(
                "effect-color border-2 border-inverse group-hover:border-default",
                voiceIndex > 0 && "-ml-16 mt-8",
                assignedVoices.length > 1 && voiceIndex === 0 && "-mt-8",
              )}
              height={20}
              image={getVoiceImage(voice, "md")}
              key={voice.voiceId}
              size='md'
              width={20}
              zIndex={assignedVoices.length - voiceIndex}
            />
          ))}
        </div>
        <span className='f-paragraph-02'>
          {assignedVoices.length > 1 ? `${assignedVoices.length} ${t("widget.speakers")}` : assignedVoices[0].name}
        </span>
        <span className='f-description-04 mx-auto w-3/5 truncate text-center text-lighter'>{description}</span>
      </div>

      <SamplePlayButton activeSampleId={activeSampleId} playingState={playingState} prompt={prompt} />
    </m.button>
  );
};

export const VoicePickerTags = ({
  voices,
  selectedLanguage,
  onSelected,
  singleLine = false,
  className,
  useV3Prompts = false,
  samplePrompts = EMPTY_SAMPLE_PROMPTS,
  initialLanguage,
  initialPrompts,
  variant = "tags",
  playingState,
  activeSampleId,
  handlePlayPause,
}: {
  activeSampleId?: string;
  className?: string;
  handlePlayPause: () => void;
  initialLanguage?: null | string;
  initialPrompts?: IUseCasePromptWithId[];
  onSelected: (prompt: IUseCasePromptWithId, voices: Voice[]) => void;
  playingState: PlayingState;
  samplePrompts?: SamplePrompt[] | null;
  selectedLanguage: string;
  singleLine?: boolean;
  useV3Prompts?: boolean;
  variant?: "cards" | "sample-picker" | "tags";
  voices: Voice[];
}) => {
  const t = useTranslations("ClientKeys.TTS");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftOverlay, setShowLeftOverlay] = useState(false);
  const [showRightOverlay, setShowRightOverlay] = useState(true);
  const [demoPrompts, setDemoPrompts] = useState<DemoPrompts | undefined>({
    samplePrompts,
    useCasePrompts: initialPrompts,
  });

  // Fetch demo prompts based on selected language
  useEffect(() => {
    const hasChangedFromDefaultLanguage = selectedLanguage !== initialLanguage;
    getPromptsPerUseCase(selectedLanguage).then(prompts => {
      setDemoPrompts({
        samplePrompts: (hasChangedFromDefaultLanguage ? [] : samplePrompts) ?? [],
        useCasePrompts: prompts,
      });
    });
  }, [selectedLanguage, samplePrompts, initialLanguage]);

  const voiceItems = useMemo(() => {
    if (!demoPrompts) return [];

    return getVoiceToUseCasePromptAssignment(voices, demoPrompts, {
      maxPrompts: useV3Prompts ? 6 : 8,
      distributeFeatures: useV3Prompts ? [PromptFeature.AUDIO_TAGS, PromptFeature.DIALOGUE] : undefined,
      includeUntagged: useV3Prompts ? false : true,
    }).map(({ assignedVoices, prompt }) => {
      return {
        assignedVoices,
        prompt,
        text: prompt.text,
        description: prompt.description,
      };
    });
  }, [demoPrompts, voices, useV3Prompts]);

  // Handle scroll detection
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      // Defer layout calculations to avoid forced reflow
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
          setShowLeftOverlay(scrollLeft > 0);
          setShowRightOverlay(scrollLeft < scrollWidth - clientWidth - 10); // 10px buffer
        }
      });
    }
  }, []);

  // Initialize scroll state and handle window resizing
  useEffect(() => {
    handleScroll();

    // Add scroll event listener
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);

      // Check initial scroll status
      setTimeout(handleScroll, 100);

      // Add window resize listener
      window.addEventListener("resize", handleScroll);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [handleScroll, voiceItems]);

  const sliderItems =
    variant === "cards"
      ? voiceItems.map(({ assignedVoices, prompt, description }, index) => {
          return (
            <SampleSliderCard
              activeSampleId={activeSampleId}
              assignedVoices={assignedVoices}
              description={description}
              handlePlayPause={handlePlayPause}
              key={index}
              onSelected={onSelected}
              playingState={playingState}
              prompt={prompt}
            />
          );
        })
      : [];

  const samplePickerItems = useMemo(() => {
    return voiceItems
      .map(prompt => ({
        label: prompt.prompt.title,
        value: prompt.prompt.id,
      }))
      .slice(0, 5);
  }, [voiceItems]);

  const handleSamplePickerSelected = useCallback(
    (sample: SampleOption) => {
      const prompt = voiceItems.find(item => item.prompt.id === sample.value);
      if (prompt) {
        onSelected(prompt.prompt, prompt.assignedVoices);
      }
    },
    [onSelected, voiceItems],
  );

  if (variant === "sample-picker") {
    return <SamplePicker className={className} onSelected={handleSamplePickerSelected} samples={samplePickerItems} />;
  }

  return (
    <div className={cn("w-full p-16 py-8 md:p-24 md:pt-16", className)}>
      <div className='relative h-full'>
        {variant === "cards" && (
          <div className='flex h-full flex-col justify-center'>
            <SliderGeneric floatingControls fullCards items={sliderItems} noBreakout />
          </div>
        )}
        {variant === "tags" && (
          <div
            className={cn("scrollbar-none flex gap-4 overflow-x-auto", !singleLine && "xl:flex-wrap")}
            ref={scrollContainerRef}
          >
            {voiceItems.map(({ assignedVoices, prompt, description }, index) => (
              <AnimatePresence initial={false} key={index + selectedLanguage}>
                <m.button
                  animate={{ opacity: 1, translateY: 0 }}
                  className={cn(
                    "effect-color group flex flex-shrink-0 items-center gap-8 rounded-lg border border-default p-8 pr-12 hover:bg-dark",
                  )}
                  initial={{ opacity: 0, translateY: 10 }}
                  onClick={() => onSelected(prompt, assignedVoices)}
                  transition={{ duration: 0.2, delay: index * 0.1 }}
                >
                  <div className='flex items-center gap-6'>
                    <div className='flex items-center'>
                      {assignedVoices.map((voice, voiceIndex) => (
                        <Avatar
                          className={cn(
                            "effect-color border-2 border-inverse group-hover:border-default",
                            voiceIndex > 0 && "-ml-8",
                          )}
                          height={14}
                          image={getVoiceImage(voice, "md")}
                          key={voice.voiceId}
                          size='xs'
                          width={14}
                          zIndex={assignedVoices.length - voiceIndex}
                        />
                      ))}
                    </div>
                    <span className='f-ui-08 font-bold'>
                      {assignedVoices.length > 1
                        ? `${assignedVoices.length} ${t("widget.speakers")}`
                        : assignedVoices[0].name}
                    </span>
                  </div>
                  <div className='h-12 w-1 bg-darker' />
                  <div className='f-ui-08 font-bold'>{description}</div>
                </m.button>
              </AnimatePresence>
            ))}
          </div>
        )}
        {variant === "tags" && (
          <>
            <div
              className={cn(
                "pointer-events-none absolute left-0 top-0 z-3 h-full w-80 bg-gradient-to-r from-[white] to-transparent transition-opacity duration-200",
                showLeftOverlay ? "opacity-100" : "opacity-0",
              )}
            />
            <div
              className={cn(
                "pointer-events-none absolute right-0 top-0 z-3 h-full w-80 bg-gradient-to-r from-transparent to-[white] transition-opacity duration-200",
                showRightOverlay ? "opacity-100" : "opacity-0",
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};
